// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, DOCUMENT } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, tap, take, takeUntil } from 'rxjs/operators';
import {
  Permission,
  User,
} from 'src/app/generated/api/model/models';
import { UserDataService } from 'src/app/data/user/user-data.service';
import { UserQuery } from 'src/app/data/user/user.query';
import { TopbarView } from 'src/app/components/shared/top-bar/topbar.models';
import { ComnSettingsService, ComnAuthQuery, Theme } from '@cmusei/crucible-common';
import { CollectionDataService } from 'src/app/data/collection/collection-data.service';
import { CollectionQuery } from 'src/app/data/collection/collection.query';
import { CurrentUserQuery } from 'src/app/data/user/user.query';
import { ExhibitDataService } from 'src/app/data/exhibit/exhibit-data.service';
import { ExhibitQuery } from 'src/app/data/exhibit/exhibit.query';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';
import { ApplicationArea, SignalRService } from 'src/app/services/signalr.service';
import { Section } from 'src/app/utilities/enumerations';
import { environment } from 'src/environments/environment';
import { HealthCheckService } from 'src/app/generated/api';
import { ComnAuthService } from '@cmusei/crucible-common';
import { SystemPermission } from 'src/app/generated/api';

@Component({
  selector: 'app-admin-container',
  templateUrl: './admin-container.component.html',
  styleUrls: ['./admin-container.component.scss'],
  standalone: false
})
export class AdminContainerComponent implements OnDestroy, OnInit {
  titleText = 'GALLERY';
  displayedSection = '';
  isSidebarOpen = true;
  userList: Observable<User[]>;
  permissionList: Observable<Permission[]>;
  pageSize: Observable<number>;
  pageIndex: Observable<number>;
  private unsubscribe$ = new Subject();
  hideTopbar = false;
  TopbarView = TopbarView;
  topbarImage = this.settingsService.settings.AppTopBarImage;
  theme$: Observable<Theme>;
  section = Section;
  uiVersion = environment.VERSION;
  apiVersion = 'ERROR!';
  username = '';
  canViewCollections = false;
  canViewExhibits = false;
  canViewUsers = false;
  canViewRoles = false;
  canViewGroups = false;
  canCreateCollections = false;
  canCreateExhibits = false;
  readonly SystemPermission = SystemPermission;
  showSection$: Observable<string>;
  originalExhibitId: string;

  constructor(
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private router: Router,
    private signalRService: SignalRService,
    private collectionDataService: CollectionDataService,
    private collectionQuery: CollectionQuery,
    private exhibitDataService: ExhibitDataService,
    private exhibitQuery: ExhibitQuery,
    private authService: ComnAuthService,
    private userDataService: UserDataService,
    private userQuery: UserQuery,
    activatedRoute: ActivatedRoute,
    private healthCheckService: HealthCheckService,
    private settingsService: ComnSettingsService,
    private authQuery: ComnAuthQuery,
    private currentUserQuery: CurrentUserQuery,
    private permissionDataService: PermissionDataService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.theme$ = this.authQuery.userTheme$;
    this.hideTopbar = this.inIframe();
    this._document.getElementById('appTitle').innerHTML = this.settingsService.settings.AppTitle + ' Admin';
    this.originalExhibitId = this.exhibitQuery.getActiveId() as string;

    // observe the collections
    this.collectionQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(collections => {
      this.permissionDataService.load().subscribe();
      this.permissionDataService.loadCollectionPermissions().subscribe();
      this.permissionDataService.loadExhibitPermissions().subscribe();
    });
    // observe active collection id
    this.collectionQuery.selectActiveId().pipe(takeUntil(this.unsubscribe$)).subscribe(activeId => {
      this.exhibitDataService.loadByCollection(activeId);
    });
    // Set the display settings from config file
    this.titleText = this.settingsService.settings.AppTopBarText;
    this.getApiVersion();
    this.showSection$ = activatedRoute.queryParamMap.pipe(
      tap(
        (params) =>
        (this.displayedSection =
          params.get('section') || Section.collections)
      ),
      map((params) => params.get('section') || Section.collections)
    );
    this.showSection$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((section) => {
        this.displayedSection = section;
      });
  }

  ngOnInit() {
    this.userList = this.userQuery.selectAll();
    this.userDataService.load().pipe(take(1)).subscribe();
    this.permissionDataService.load().pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.updatePermissions();
      if (this.permissionDataService.shouldLoadAllCollections()) {
        this.collectionDataService.load();
      } else {
        this.collectionDataService.loadMine();
      }
    });
    this.permissionDataService.loadCollectionPermissions().subscribe();
    this.permissionDataService.loadExhibitPermissions().subscribe();
    this.signalRService
      .startConnection(ApplicationArea.admin)
      .then(() => {
        this.signalRService.join();
      })
      .catch((err) => {
        console.log(err);
      });
    this.currentUserQuery
      .select()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cu) => {
        this.username = cu.name;
      });
    this.userDataService.setCurrentUser();
  }

  updatePermissions() {
    this.canViewCollections = this.permissionDataService.hasPermission(SystemPermission.ViewCollections)
      || this.permissionDataService.hasPermission(SystemPermission.EditCollections)
      || this.permissionDataService.hasPermission(SystemPermission.ManageCollections);
    this.canViewExhibits = this.permissionDataService.hasPermission(SystemPermission.ViewExhibits)
      || this.permissionDataService.hasPermission(SystemPermission.EditExhibits)
      || this.permissionDataService.hasPermission(SystemPermission.ManageExhibits);
    this.canViewUsers = this.permissionDataService.hasPermission(SystemPermission.ViewUsers);
    this.canViewRoles = this.permissionDataService.hasPermission(SystemPermission.ViewRoles);
    this.canViewGroups = this.permissionDataService.hasPermission(SystemPermission.ViewGroups);
    this.canCreateCollections = this.permissionDataService.hasPermission(SystemPermission.CreateCollections);
    this.canCreateExhibits = this.permissionDataService.hasPermission(SystemPermission.CreateExhibits);
    this.changeDetectorRef.detectChanges();
  }

  exitAdmin() {
    this.router.navigate([''], {
      queryParams: { exhibit: this.originalExhibitId || undefined },
    });
  }

  gotoSection(section: Section) {
    this.displayedSection = section;
    this.router.navigate([], {
      queryParams: {
        section: section
      },
      queryParamsHandling: 'merge',
    });
  }

  logout() {
    this.authService.logout();
  }

  getSelectedClass(section: string) {
    if (section === this.displayedSection) {
      return 'selected-item';
    } else {
      return 'showhand';
    }
  }
  inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  getApiVersion() {
    this.healthCheckService
      .getVersion()
      .pipe(take(1))
      .subscribe(
        (message) => {
          const messageParts = message.split('+');
          this.apiVersion = messageParts[0];
        },
        (error) => {
          this.apiVersion = 'ERROR!';
        }
      );
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
